import type { PropertyId } from "."
import type { BaseEntity, Entity, EntityProperty } from "./BaseEntity"

import { PropertyStatus } from "."

type Constructor<EP extends EntityProperty, E extends Entity<EP>> = abstract new (...args: any[]) => BaseEntity<EP, E>

export function withStatic<
  EP extends EntityProperty,
  E extends Entity<EP>,
  B extends Constructor<EP, E>,
>(Base: B) {
  abstract class StaticEntity extends Base {
    abstract get _inner(): E
    abstract set _inner(value: E)

    mergeWithNewer<T = BaseEntity<EP, E>>(later?: T): boolean {
      const newer = later as BaseEntity<EP, E> | undefined
      if (!newer)
        return false

      if (newer.id !== this.inner().id)
        throw new Error("Cannot merge conversations with different conversation ids!")

      // merge the selections first
      this.selection.mergedWith(newer.selection)

      let changed = false
      for (const prop of Object.values(newer.inner().properties)) {
        const thisProp = this.inner().properties[prop!.propertyId]
        if (!prop)
          continue
        else if (!thisProp) {
          this._inner.properties[prop!.propertyId] = prop
          changed = true
        }
        else if (thisProp && (thisProp.epochId ?? 0) < (prop.epochId ?? 0)) {
          this._inner.properties[prop!.propertyId] = prop
          changed = true
        }
        else if (thisProp && thisProp.status !== PropertyStatus.StatusCurrent && prop.status === PropertyStatus.StatusCurrent) {
          this._inner.properties[prop!.propertyId] = prop
          changed = true
        }
        else if (thisProp && (thisProp.version ?? 0) < (prop.version ?? 0)) {
          this._inner.properties[prop!.propertyId] = prop
          changed = true
        }
        else if (thisProp && (thisProp.timestamp) < (prop.timestamp!)) {
          this._inner.properties[prop!.propertyId] = prop
          changed = true
        }
      }
      return changed
    }

    retain(properties: PropertyId[]) {
      const propertySet = new Set(properties)
      for (const propertyId of Object.keys(this.inner().properties))
        if (propertySet.has(propertyId as PropertyId))
          continue
        else
          delete this._inner.properties[propertyId]
      return this
    }

    /**
     * Changes the status of all properties to the given status. Should only be used during
     * testing.
     * TODO: error if we are not in testing
     */
    changeToStatus(status: PropertyStatus) {
      for (const propertyId in this._inner.properties)
        this._inner.properties[propertyId]!.status = status
    }

    addProperty(property: EP) {
      property.id = this.id
      this._inner.properties[property.propertyId] = property
      this.selection.addPropertyId(property.propertyId as PropertyId)
    }

    /**
     * TODO: remove the property from the selection as well
     */
    removeProperty(propertyId: PropertyId) {
      delete this._inner.properties[propertyId]
    }

    setId(id: string) {
      this._inner.id = id
      for (const property of Object.values(this._inner.properties))
        property!.id = id
    }
  }
  return StaticEntity
}

export type StaticEntity<EP extends EntityProperty, E extends Entity<EP>> = InstanceType<ReturnType<typeof withStatic<EP, E, Constructor<EP, E>>>> & BaseEntity<EP, E>
